import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';
import { noop, switchMap, tap } from 'rxjs';

import {
  Company,
  ILegacyList,
  IUser,
  GlobalItemModels,
  ListCSVParserService,
  Project,
} from '@site-mate/dashpivot-shared-library';

import { FileUploadDropzoneComponent } from 'app/shared/component/file-upload-dropzone/file-upload-dropzone.component';
import { WarningModalComponent } from 'app/shared/component/warning-modal/warning-modal.component';
import { AppHierarchy } from 'app/shared/model/app-hierarchy.enum';
import { ExportResponse } from 'app/shared/model/export-response.model';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { UserService } from 'app/user/user.service';

import { ISignedPostResponse, ListImportService } from '../list-import.service';

interface IFolder {
  hierarchy: Exclude<AppHierarchy, 'team'>;
  project?: Project;
  company?: Company;
}

@Component({
  selector: 'cc-list-import-modal',
  templateUrl: './list-import-modal.component.html',
  imports: [CommonModule, FileUploadDropzoneComponent],
  standalone: true,
  styleUrls: ['./list-import-modal.component.scss'],
})
export class ListImportModalComponent implements OnInit {
  @Input() list: ILegacyList;
  @Input() folder: IFolder;
  canImport = false;
  validating = false;
  isImporting = false;
  readonly maxListRows = ListCSVParserService.maxListRows;

  listErrorsByColumnMap: Map<number, { kind: GlobalItemModels; count: number }> = new Map();
  blockingError = '';
  private user: IUser;
  private file: File;
  private sessionParameters: ExportResponse;

  constructor(
    public modal: NgbActiveModal,
    private readonly toastrService: ToastrService,
    private readonly listImportService: ListImportService,
    private readonly errorHandler: ErrorHandler,
    private readonly userService: UserService,
    private readonly modalService: NgbModal,
  ) {}

  ngOnInit(): void {
    this.user = this.userService.getCurrentUser();
  }

  private clearValidation() {
    this.canImport = false;
    this.validating = true;
    this.listErrorsByColumnMap.clear();
    this.blockingError = '';
    this.file = null;
  }

  enableImport(file: File) {
    this.clearValidation();

    const csvImportWorker = new Worker(new URL('./list-csv-import.worker', import.meta.url), {
      type: 'module',
    });

    csvImportWorker.postMessage({ file, list: this.list });
    csvImportWorker.onmessage = (event) => {
      const { complete, listErrorsByColumnMap, error } = event.data;

      if (complete) {
        this.listErrorsByColumnMap = listErrorsByColumnMap;
        this.file = file;
        this.canImport = true;
      }

      if (error) {
        this.blockingError = error;
        this.canImport = false;
      }

      this.validating = false;
      csvImportWorker.terminate();
    };
  }

  handleDropzoneError({ message }) {
    if (message === 'You can not upload any more files.') {
      this.toastrService.errorByKey('importCSVMaxFilesError');
    }
  }

  downloadErrors() {
    const entriesSorted = Array.from(this.listErrorsByColumnMap.entries()).sort((a, b) => a[0] - b[0]);
    const errors = entriesSorted
      .filter(([_column, details]) => details.kind !== GlobalItemModels.AttachmentSimple)
      .map(([column, details]) => {
        const { kind, count } = details;
        return `${this.getColumnChar(column)},${count},${this.mapKindToErrorType(kind)}`;
      });

    const csvContent = `Column,Number of Cells Impacted,Error Type\n${errors.join('\n')}`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    saveAs(blob, 'errors.csv');
  }

  private mapKindToErrorType(kind: string) {
    const errorMap = {
      HexCode: 'Invalid hex code formating detected. Valid formatting is #000000.',
      [GlobalItemModels.NumberSimple]:
        'Unsupported characters in number property detected. Only support numerical.',
      [GlobalItemModels.DateSimple]: 'Invalid date formatting detected. Valid formatting is DD/MM/YYYY.',
      [GlobalItemModels.DateExpiry]: 'Invalid date formatting detected. Valid formatting is DD/MM/YYYY.',
    };

    return errorMap[kind];
  }

  sendFileToServer() {
    return this.listErrorsByColumnMap.size > 0 ? this.openWarningModal() : this.uploadFile();
  }

  private openWarningModal(): Promise<void> {
    const modalOptions = { centered: true, windowClass: 'sm-flex sm-items-center' };

    const modalRef = this.modalService.open(WarningModalComponent, modalOptions);

    modalRef.componentInstance.titleKey = 'listInvalidFormattingTitle';
    modalRef.componentInstance.messageKey = 'listInvalidFormattingBody';
    modalRef.componentInstance.continueLabel = 'Continue Upload';

    return modalRef.result.then(() => this.uploadFile()).catch(noop);
  }

  private uploadFile() {
    this.isImporting = true;
    this.listImportService
      .getPresignedS3URL(this.folder, this.list._id)
      .pipe(
        tap(this.setSessionParameters.bind(this)),
        // @ts-expect-error implicitly has an 'any' type.

        switchMap((response) => this.sendFileToS3(response.signedPostResponse)),
      )
      .subscribe({
        next: () => this.modal.close(this.sessionParameters),
        error: (err) => this.errorHandler.handle(err),
      });
  }

  private getColumnChar(index: number) {
    return String.fromCharCode(65 + index);
  }

  private setSessionParameters({ sessionParameters }: { sessionParameters: ExportResponse }) {
    this.sessionParameters = sessionParameters;
  }

  private sendFileToS3(signedPostResponse: ISignedPostResponse) {
    const params = {
      signedPostResponse,
      sessionId: this.sessionParameters.id,
      file: this.file,
      listId: this.list._id,
      user: this.user,
    };

    return this.listImportService.uploadFileToS3(params);
  }
}
